import * as React from "react";
import { BookOpen, Users, FileText, Landmark, ChartLine, Settings } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
const categories = [{
  id: 1,
  title: "Bookkeeping & Recordkeeping",
  iconName: "BookOpen"
}, {
  id: 2,
  title: "Payroll Services",
  iconName: "Users"
}, {
  id: 3,
  title: "Sales Tax Compliance",
  iconName: "FileText"
}, {
  id: 4,
  title: "Corporate & Personal Tax",
  iconName: "Landmark"
}, {
  id: 5,
  title: "Financial Reporting & Advisory",
  iconName: "ChartLine"
}, {
  id: 6,
  title: "Setup & One-Time Services",
  iconName: "Settings"
}];
const services = [{
  id: 101,
  categoryId: 1,
  name: "Monthly bookkeeping",
  description: "Transaction categorization, bank & credit card reconciliation, and CRA-ready records.",
  price: "Starting at CAD $249/month"
}, {
  id: 102,
  categoryId: 1,
  name: "Accounts payable & receivable",
  description: "Invoice processing, aging reports, and payment follow-up to keep your cash cycle moving.",
  price: "Starting at CAD $149/month"
}, {
  id: 103,
  categoryId: 1,
  name: "Management financial statements",
  description: "Year-end income statement, balance sheet, and cash flow — suitable for internal use and owner review.",
  price: "Starting at CAD $399/year"
}, {
  id: 104,
  categoryId: 1,
  name: "Year-end cleanup",
  description: "Catch-up and cleanup of neglected books ahead of tax season or a financial review.",
  price: "Starting at CAD $75/hour"
}, {
  id: 201,
  categoryId: 2,
  name: "Payroll processing",
  description: "Full-cycle payroll with CRA remittances, ROEs, and direct deposit setup.",
  price: "Starting at CAD $129/month"
}, {
  id: 202,
  categoryId: 2,
  name: "T4 / T4A preparation & filing",
  description: "Year-end employee and contractor slip preparation and CRA submission.",
  price: "Starting at CAD $149/year (up to 5 employees)"
}, {
  id: 203,
  categoryId: 2,
  name: "Employee & contractor onboarding",
  description: "TD1 setup, payroll registration, and CRA payroll account management.",
  price: "Starting at CAD $75/employee"
}, {
  id: 301,
  categoryId: 3,
  name: "GST/HST filing & remittance",
  description: "Return preparation and CRA submission on your filing frequency, with correspondence support.",
  price: "Starting at CAD $99/month"
}, {
  id: 302,
  categoryId: 3,
  name: "GST/HST registration",
  description: "CRA registration for new businesses crossing the $30,000 threshold or registering voluntarily.",
  price: "Flat fee: CAD $149"
}, {
  id: 303,
  categoryId: 3,
  name: "PST / QST compliance",
  description: "Provincial sales tax filing support for BC, Saskatchewan, Manitoba, and Quebec.",
  price: "Starting at CAD $99/month"
}, {
  id: 401,
  categoryId: 4,
  name: "Corporate tax return (T2)",
  description: "Annual T2 preparation focused on eligible deductions, CCA, and small business credits.",
  price: "Starting at CAD $999/year"
}, {
  id: 402,
  categoryId: 4,
  name: "Corporate tax planning",
  description: "Salary vs. dividend optimization, holdco structuring, and tax deferral strategies.",
  price: "Starting at CAD $499/engagement"
}, {
  id: 403,
  categoryId: 4,
  name: "Personal tax return (T1)",
  description: "Individual returns including employment, investment, and rental income.",
  price: "Starting at CAD $149/return"
}, {
  id: 404,
  categoryId: 4,
  name: "Self-employed return (T1 + T2125)",
  description: "Comprehensive T1 with business income schedules and full expense optimization.",
  price: "Starting at CAD $299/return"
}, {
  id: 405,
  categoryId: 4,
  name: "Rental income reporting (T776)",
  description: "Rental property schedules with CCA and eligible expense claims.",
  price: "Starting at CAD $199/return"
}, {
  id: 406,
  categoryId: 4,
  name: "CRA audit & correspondence support",
  description: "Representation and response management for CRA reviews and reassessments.",
  price: "Billed at CAD $150/hour"
}, {
  id: 501,
  categoryId: 5,
  name: "Monthly financial statements",
  description: "Balance sheet, P&L, and cash flow delivered monthly to support decision-making.",
  price: "Starting at CAD $179/month"
}, {
  id: 502,
  categoryId: 5,
  name: "Budget vs. actuals reporting",
  description: "Monthly variance analysis with commentary on key performance drivers.",
  price: "Starting at CAD $149/month"
}, {
  id: 503,
  categoryId: 5,
  name: "Cash flow management",
  description: "Proactive monitoring of receivables, payables, and liquidity with forward-looking projections.",
  price: "Starting at CAD $199/month"
}, {
  id: 504,
  categoryId: 5,
  name: "Fractional CFO services",
  description: "Strategic financial oversight, KPI dashboards, and lender-ready reporting — without the full-time hire.",
  price: "Starting at CAD $599/month"
}, {
  id: 601,
  categoryId: 6,
  name: "Business registration & incorporation",
  description: "",
  price: "Flat fee: CAD $349 + government fees"
}, {
  id: 602,
  categoryId: 6,
  name: "Chart of accounts setup",
  description: "",
  price: "Flat fee: CAD $299–$499"
}, {
  id: 603,
  categoryId: 6,
  name: "Software migration & onboarding",
  description: "",
  price: "Starting at CAD $299"
}, {
  id: 604,
  categoryId: 6,
  name: "CRA business number registration",
  description: "",
  price: "Flat fee: CAD $99"
}];
const iconMap: Record<string, React.ElementType> = {
  BookOpen,
  Users,
  FileText,
  Landmark,
  ChartLine,
  Settings
};
export default function ALaCarteServices() {
  return <section id="à-la-carte-services" className="bg-background py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        {categories.map((category, i) => {
        const Icon = iconMap[category.iconName];
        const categoryServices = services.filter(s => s.categoryId === category.id);
        const isCompact = category.id === 6;
        return <div key={category.id} data-index={i} className="mb-16 last:mb-0">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                  <Icon className="h-5 w-5 text-primary" />
                </div>
                <h3 className="text-2xl md:text-3xl font-semibold font-default text-foreground">
                  {category.title}
                </h3>
              </div>
              
              <Separator className="mb-8 bg-border/60" />

              <div className={cn("grid gap-6", isCompact ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4" : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3")}>
                {categoryServices.map((service, j) => <Card key={service.id} data-index={j} className={cn("flex flex-col h-full transition-shadow hover:shadow-lg", isCompact ? "bg-muted/30 shadow-sm border-border/50" : "shadow-md")}>
                    <CardHeader className={cn(isCompact && "pb-3")}>
                      <CardTitle className={cn("font-default leading-tight", isCompact ? "text-base" : "text-xl")}>
                        {service.name}
                      </CardTitle>
                    </CardHeader>
                    
                    {!isCompact && service.description && <CardContent className="flex-grow">
                        <p className="text-muted-foreground text-sm leading-relaxed">
                          {service.description}
                        </p>
                      </CardContent>}
                    
                    <CardFooter className={cn("mt-auto pt-4", isCompact && "pt-0")}>
                      <Badge variant="secondary" className="font-medium text-xs sm:text-sm whitespace-normal text-left h-auto py-1.5 px-3 bg-secondary/10 text-secondary-foreground hover:bg-secondary/20">
                        {service.price}
                      </Badge>
                    </CardFooter>
                  </Card>)}
              </div>
            </div>;
      })}
      </div>
    </section>;
}